// Appeal Results Summary — board decisions and realised reductions, by jurisdiction.
//
// ⛔ ON CURRENT DATA THIS RENDERS A COLUMN OF ZEROS, AND THAT IS THE CORRECT ANSWER.
// Every accepted decision in this portfolio is accepted-AT-PROPOSED: the board agreed with the
// assessor's number, so the realised reduction is nought. 58 decisions, $0 realised, one
// jurisdiction. There is deliberately NO fallback, NO placeholder and NO empty state that hides
// it — a client reading "58 decisions, $0 realised reduction" is reading the truth about this
// portfolio, and a report that dressed that up would be worse than useless.
//
// The distinction the layer already enforces matters here more than anywhere: a realised reduction
// of ZERO (the board decided, and the value did not move) is a different statement from BLANK
// (no decision, so nothing to realise). This report shows both, and never converts one into the
// other.
//
// GRAIN. Detail rows are parcel-years that HAVE a decision — the same columns as the executive
// summary, so a reader moving between the two reports is reading the same shape. The grouped
// statistics (by jurisdiction, then appeal level) live in the summary block and in their own
// export sheet, because ReportView renders a flat table by design and a "group header" row mixed
// into the data would be filtered, sorted and totalled as though it were data.

import { PARCEL_YEAR_COLUMNS, fetchParcelYearScope, buildParcelYearRows, NOTICE_OPTIONS } from '../parcelYearRows';
import { appealLevelLabel } from '../appealLevels';
import { toNumber, sumColumn } from '../format';
import { assertRowFields } from '../grouping';

const asOfLabel = (d = new Date()) => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
const titleRows = (clientName, heading) => [null, clientName || '', heading, `As of ${asOfLabel()}`, null, null];

const UNSTATED = '(no jurisdiction recorded)';
const jurOf = (r) => r.jurisdiction || UNSTATED;

// Value appealed = the ASSESSOR'S number, which is what was contested. Realised reduction is what
// came off it. Both on the taxable basis the rate applies to, which buildParcelYearRows resolved.
const appealed = (r) => toNumber(r.proposed_value);
// ⛔ NTRG's realised reduction: a Non-NTRG Appeal One's reduction is not NTRG's (step 7) — ntrg_reduction is the whole
// reduction everywhere else. The detail rows still show the full Value Reduction, marked Non-NTRG.
const realised = (r) => toNumber(r.ntrg_reduction);

function groupStats(rows) {
  const m = new Map();
  rows.forEach((r) => {
    const k = `${jurOf(r)}|${r.decision_level || ''}`;
    const g = m.get(k) || {
      jurisdiction: jurOf(r), level: appealLevelLabel(r.decision_level) || '—',
      decisions: 0, outcomes: new Map(), appealed: 0, realised: 0, hasAppealed: false, hasRealised: false,
    };
    g.decisions += 1;
    const outcome = r.decision || '(unrecorded)';
    g.outcomes.set(outcome, (g.outcomes.get(outcome) || 0) + 1);
    const a = appealed(r); if (a != null) { g.appealed += a; g.hasAppealed = true; }
    const v = realised(r); if (v != null) { g.realised += v; g.hasRealised = true; }
    m.set(k, g);
  });
  return [...m.values()]
    .map((g) => ({
      jurisdiction: g.jurisdiction,
      level: g.level,
      decisions: g.decisions,
      outcome: [...g.outcomes.entries()].map(([k, n]) => `${k} ${n}`).join(', '),
      // ⛔ Blank, not zero, when NOTHING in the group carried a figure — distinct from a real 0.
      appealed: g.hasAppealed ? g.appealed : null,
      realised: g.hasRealised ? g.realised : null,
      // A percent of an absent or zero base is unanswerable, not 0.
      pct: g.hasRealised && g.hasAppealed && g.appealed !== 0 ? (g.realised / g.appealed) * 100 : null,
    }))
    .sort((a, b) => a.jurisdiction.localeCompare(b.jurisdiction) || String(a.level).localeCompare(String(b.level)));
}

export const appealResultsSummary = {
  id: 'appeal_results',
  title: 'Appeal Results Summary',
  description: 'Board decisions and realised reductions by jurisdiction.',

  filters: [
    'client',
    'years',
    { key: 'basis', type: 'toggle', label: 'Basis', default: 'taxable',
      options: [{ value: 'taxable', label: 'Taxable' }, { value: 'market', label: 'Market' }] },
    { key: 'jurisdiction', type: 'enum', label: 'Jurisdiction', from: 'jurisdiction' },
    { key: 'level', type: 'enum', label: 'Level', from: 'appeal_level' },
    { key: 'state', type: 'enum', label: 'State', from: 'state' },
    { key: 'property', type: 'enum', label: 'Property', from: 'property', picker: 'search', noun: 'properties' },
    ...NOTICE_OPTIONS,
  ],

  columns: PARCEL_YEAR_COLUMNS,

  fetch: (ctx) => fetchParcelYearScope(ctx, 'appeal results'),

  buildRows(raw, ctx) {
    const all = buildParcelYearRows(raw, ctx);
    // Grouped by jurisdiction and decision level in the summary and the export sheet.
    assertRowFields(all, ['jurisdiction', 'decision', 'decision_level', 'proposed_value', 'reduction', 'ntrg_reduction'], 'appeal results');
    const decided = all.filter((r) => r.decision);
    if (!decided.length && all.length) {
      console.info(`[appeal results] no decisions recorded in ${all.length} account-year(s) for this selection`);
    }
    return decided;
  },

  // Decisions and realised reduction BY JURISDICTION, plus a total. Computed from the FILTERED
  // rows, so narrowing the table moves these with it.
  summary(rows) {
    const byJur = new Map();
    rows.forEach((r) => {
      const g = byJur.get(jurOf(r)) || { decisions: 0, realised: 0, has: false };
      g.decisions += 1;
      const v = realised(r); if (v != null) { g.realised += v; g.has = true; }
      byJur.set(jurOf(r), g);
    });
    const money = (n) => (n == null ? '—' : `$${Math.round(n).toLocaleString()}`);
    const tiles = [...byJur.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([j, g]) => ({
      label: j,
      value: `${g.decisions} decision${g.decisions === 1 ? '' : 's'}`,
      // ⛔ "$0 realised" is printed, not suppressed. It is the finding.
      sub: `${money(g.has ? g.realised : null)} realised`,
    }));
    const total = [...byJur.values()].reduce((s, g) => ({
      decisions: s.decisions + g.decisions, realised: s.realised + g.realised, has: s.has || g.has,
    }), { decisions: 0, realised: 0, has: false });
    return tiles.length
      ? [...tiles, { label: 'Total', value: `${total.decisions} decision${total.decisions === 1 ? '' : 's'}`, sub: `${money(total.has ? total.realised : null)} realised` }]
      : [];
  },

  exportSheets({ rows, columns, totals, footnotes, ctx }) {
    const client = ctx?.clientName || '';
    const stats = groupStats(rows);
    return [
      {
        name: 'By Jurisdiction',
        titleRows: titleRows(client, 'Appeal Results Summary'),
        columns: [
          { key: 'jurisdiction', label: 'Jurisdiction', type: 'text' },
          { key: 'level', label: 'Level', type: 'text' },
          { key: 'decisions', label: 'Decisions', type: 'int' },
          { key: 'outcome', label: 'Outcomes', type: 'text' },
          { key: 'appealed', label: 'Value Appealed', type: 'value' },
          { key: 'realised', label: 'Realised Reduction', type: 'value' },
          { key: 'pct', label: 'Realised %', type: 'percent' },
        ],
        rows: stats,
        totals: {
          jurisdiction: 'TOTAL',
          decisions: sumColumn(stats, 'decisions'),
          appealed: sumColumn(stats, 'appealed'),
          realised: sumColumn(stats, 'realised'),
        },
        footnotes,
      },
      { name: 'Detail', titleRows: titleRows(client, 'Appeal Results Summary'), columns, rows, totals: totals || undefined, footnotes },
    ];
  },

  // ⛔ THE EMPTY STATE MATTERS MORE HERE THAN ANYWHERE. This report shows only parcel-years with a
  // recorded decision, which is a much smaller set than the other three — so "nothing here" is the
  // normal result of an ordinary filter, not a fault, and it has to say which of several reasons
  // applies rather than leaving a blank panel to be read as a broken screen.
  emptyMessage:
    'No recorded decisions for this selection. This report shows only parcel-years where a board '
    + 'decision has been entered — appeals still open, years with no appeal, and parcel-years outside '
    + 'the client’s representation are all excluded. Widen the years, clear the jurisdiction or '
    + 'level filters, or check whether decisions have been recorded for this client yet.',
};

export default appealResultsSummary;
